import React, { useContext, useState } from 'react';
import {
  CircularProgress,
  Container,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import TopologyColumn from './topologyColumn';
import {
  getAmount,
  getHighestConnectionValue,
  JSONRecordData
} from '../Log/graph.util';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { FormattedMessage } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileEnergyHub, { HubRole } from './MobileEnergyHub';

interface TopologySodistoreHomeProps {
  values: JSONRecordData;
  connected: boolean;
  loading: boolean;
  batteryClusterNumber:number;
}

function TopologySodistoreHome(props: TopologySodistoreHomeProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }
  const highestConnectionValue =
    props.values != null ? getHighestConnectionValue(props.values) : 0;

  const { product, setProduct } = useContext(ProductIdContext);

  const [showValues, setShowValues] = useState(false);

  const handleSwitch = () => () => {
    setShowValues(!showValues);
  };

  const isMobile = window.innerWidth <= 1490;
  const isPhone = useIsMobile();

  const inv = props.values?.InverterRecord;
  const hasDevices = !!inv?.Devices;

  const growattActiveIndices: number[] = hasDevices
    ? []
    : Array.from({ length: props.batteryClusterNumber }, (_, i) => i + 1)
        .filter((i) => Number(inv?.[`Battery${i}Voltage`]) > 0);

  const totalBatteryPower: number = hasDevices
    ? (inv?.TotalBatteryPower ?? 0)
    : growattActiveIndices.reduce(
        (sum, i) => sum + (Number(inv?.[`Battery${i}Power`]) || 0),
        0
      );

  const pvPower: number = hasDevices
    ? (inv?.TotalPhotovoltaicPower ?? 0)
    : (inv?.PvPower ?? 0);

  const totalLoadPower: number = hasDevices
    ? (inv?.TotalLoadPower ?? 0)
    : (inv?.ConsumptionPower ?? 0);

  const totalGridPower: number =
    inv?.TotalGridPower ?? inv?.GridPower ?? 0;

  const avgBatterySoc: number = hasDevices
    ? (inv?.AvgBatterySoc ?? 0)
    : (growattActiveIndices.length
        ? growattActiveIndices.reduce(
            (sum, i) => sum + (Number(inv?.[`Battery${i}Soc`]) || 0),
            0
          ) / growattActiveIndices.length
        : 0);

  // Phone view: the five boxes reduced to the four roles the hub renders. Signs follow
  // the desktop diagram's convention (positive = rightward there), so grid positive
  // means importing and battery positive means charging.
  const hubRoles: HubRole[] = [
    { key: 'pv', power: pvPower },
    { key: 'grid', power: totalGridPower },
    { key: 'battery', power: totalBatteryPower, soc: avgBatterySoc },
    { key: 'loads', power: totalLoadPower }
  ];

  return (
    <Container maxWidth="xl" style={{ backgroundColor: 'white' }}>
      <Grid container>
        {!props.connected && !props.loading && (
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress size={60} style={{ color: '#ffc04d' }} />
            <Typography
              variant="body2"
              style={{ color: 'black', fontWeight: 'bold' }}
              mt={2}
            >
              <FormattedMessage id="unableToCommunicate" defaultMessage="Unable to communicate with the installation" />
            </Typography>
            <Typography variant="body2" style={{ color: 'black' }}>
              <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
            </Typography>
          </Container>
        )}

        {props.connected && (
          <>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                marginTop: '10px',
                height: '20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'right',
                justifyContent: 'right'
              }}
            >
              {/*<div>*/}
              {/*  <Typography sx={{ marginTop: '5px', marginRight: '20px' }}>*/}
              {/*    Display Values*/}
              {/*  </Typography>*/}
              {/*  <Switch*/}
              {/*    edge="start"*/}
              {/*    color="secondary"*/}
              {/*    onChange={handleSwitch()}*/}
              {/*    sx={{*/}
              {/*      '& .MuiSwitch-thumb': {*/}
              {/*        backgroundColor: 'orange'*/}
              {/*      },*/}
              {/*      marginLeft: '20px'*/}
              {/*    }}*/}
              {/*  />*/}
              {/*</div>*/}
            </Grid>

            {isPhone ? (
              <Grid item xs={12}>
                <MobileEnergyHub roles={hubRoles} />
              </Grid>
            ) : (
            <Grid
              item
              xs={12}
              md={12}
              style={{
                height: isPhone ? 'auto' : isMobile ? '550px' : '600px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                // Phone: wrap wide diagrams to more rows so everything is visible
                // without horizontal scrolling; desktop stays one line.
                flexWrap: isPhone ? 'wrap' : 'nowrap',
                rowGap: isPhone ? '14px' : undefined
              }}
            >
              <TopologyColumn
                centerBox={{
                  title: 'Grid',
                  data: props.values?.InverterRecord
                    ? [
                      {
                        value: totalGridPower,
                        unit: 'W'
                      }
                    ]
                    : undefined,
                  connected:
                    true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.InverterRecord
                    ? {
                      value: totalGridPower,
                      unit: 'W'
                    }
                    : undefined,
                  amount: props.values?.InverterRecord
                    ? getAmount(
                      highestConnectionValue,
                      totalGridPower
                    )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={true}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'PV',
                  data: props.values?.InverterRecord
                    ? [
                      {
                        value: pvPower,
                        unit: 'W'
                      }
                    ]
                    : undefined,
                  connected: true
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.InverterRecord
                    ? {
                      value: pvPower,
                      unit: 'W'
                    }
                    : undefined,
                  amount: props.values?.InverterRecord ? getAmount(highestConnectionValue, pvPower) : 0,
                  showValues: showValues
                }}
              centerBox={{
                  title: 'Inverter',
                  data: props.values?.InverterRecord
                    ? [
                      {
                        value: 0,
                        unit: 'W'
                      }
                    ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.InverterRecord
                    ? {
                      value: totalBatteryPower,
                      unit: 'W'
                    }
                    : undefined,
                  amount: props.values?.InverterRecord
                    ? getAmount(highestConnectionValue, totalBatteryPower)
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'Loads',
                  data: props.values?.InverterRecord
                    ? [
                      {
                        value: totalLoadPower,
                        unit: 'W'
                      }
                    ]
                    : undefined,
                  connected:true
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.InverterRecord
                    ? {
                      value: totalLoadPower,
                      unit: 'W'
                    }
                    : undefined,
                  amount: props.values?.InverterRecord
                    ? getAmount(
                      highestConnectionValue,
                      totalLoadPower
                    )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                centerBox={{
                  title: 'Battery',
                  data: inv
                    ? [
                      { value: avgBatterySoc, unit: '%' },
                      { value: totalBatteryPower, unit: 'W' }
                    ]
                    : undefined,
                  connected: true
                }}
                isFirst={false}
                isLast={true}
              />
            </Grid>
            )}
          </>
        )}
      </Grid>
    </Container>
  );
}

export default TopologySodistoreHome;
